<template>
  <div v-if="!$apollo.queries.me.loading && !$apollo.queries.getFlowMetadata.loading">
    <FlowHeading
      :title="$t('Select your business entity type')"
      title-type="title1"
      :sub-title="
        $t(
          'Letting us know about your business entity setup is important to show the correct information on your legal documents, so you don’t need to worry about compliance.',
        )
      "
    />

    <BusinessEntityTypeInput market="SG">
      <div class="tw-mt-4">
        <div v-if="!canChange" class="tw-mb-4">
          <BusinessEntityCannotChangeAlert />
        </div>
        <div class="tw-my-5">
          <SgBusinessEntityTypeRadioGroup
            v-model="selectedBusinessEntity"
            :disabled="!canChange"
            name="business_entity_type"
          />
        </div>
      </div>
    </BusinessEntityTypeInput>

    <div class="wds-input-group tw-mt-8">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
  </div>
</template>

<script>
import { WdsButton } from '@worksome/wds-components'
import gql from 'graphql-tag'
import ButtonSpinner from '@/components/ButtonSpinner.vue'
import BusinessEntityCannotChangeAlert from '@/components/compliances/BusinessEntity/BusinessEntityCannotChangeAlert.vue'
import BusinessEntityTypeInput from '@/components/compliances/BusinessEntity/BusinessEntityTypeInput.vue'
import SgBusinessEntityTypeRadioGroup from '@/components/compliances/BusinessEntity/Sg/SgBusinessEntityTypeRadioGroup.vue'
import FlowHeading from '@/components/flows/FlowHeading.vue'

export default {
  name: 'SgBusinessEntityTypeInput',

  components: {
    BusinessEntityCannotChangeAlert,
    BusinessEntityTypeInput,
    ButtonSpinner,
    SgBusinessEntityTypeRadioGroup,
    FlowHeading,
    WdsButton,
  },

  inject: ['skip', 'next'],

  data() {
    return {
      selectedBusinessEntityFromMetadata: undefined,
      existingBusinessEntityFromMetadata: undefined,
      compliance: undefined,
      isSubmitting: false,
    }
  },
  computed: {
    formIsValid() {
      return (
        this.selectedBusinessEntity === 'sg_limited' ||
        this.selectedBusinessEntity === 'sg_sole_trader'
      )
    },
    selectedBusinessEntity: {
      get() {
        return this.selectedBusinessEntityFromMetadata || this.existingBusinessEntityFromMetadata
      },
      set(val) {
        this.selectedBusinessEntityFromMetadata = val
      },
    },
    canChange() {
      const data = JSON.parse(this?.compliance?.data ?? '{}')
      return data?.canChange
    },
    lacksRequiredFields() {
      return !this.formIsValid || !this.canChange
    },
    canSubmit() {
      return !this.isSubmitting && !this.lacksRequiredFields
    },
  },

  apollo: {
    me: {
      query: gql`
        query me {
          me {
            activeAccount {
              globalId
              primaryBusinessEntity {
                entityType
              }
              compliances(names: [FREELANCER_SG_BUSINESS_ENTITY]) {
                name
                isApplicable
                isComplete
                data
              }
            }
          }
        }
      `,
      result({ data }) {
        this.existingBusinessEntityFromMetadata =
          data.me?.activeAccount?.primaryBusinessEntity?.entityType
        this.compliance = data.me?.activeAccount?.compliances?.[0]
      },
      update: (data) => data.me,
    },
    getFlowMetadata: {
      query: gql`
        query getFlowMetadata($input: FlowMetadataRequest!) {
          getFlowMetadata(input: $input) {
            key
            data
          }
        }
      `,
      variables() {
        return {
          input: {
            globalId: this?.me?.activeAccount?.globalId,
            flowIdentifier: this.$route.query.flow,
            key: 'tc_sg_selected_business_entity_type',
          },
        }
      },
      result({ data }) {
        this.selectedBusinessEntityFromMetadata = JSON.parse(
          data.getFlowMetadata.data,
        ).selectedBusinessEntity
      },
      update: (data) => data.getFlowMetadata,
      skip() {
        return !this.me
      },
    },
  },

  methods: {
    async callNextScreen() {
      const promises = [this.saveSelectedBusinessEntityTypeInput()]
      this.isSubmitting = true
      await Promise.allSettled(promises)
      this.next()
    },

    async saveSelectedBusinessEntityTypeInput() {
      await this.$apollo.mutate({
        mutation: gql`
          mutation update($input: FlowMetadataInput!) {
            saveFlowMetadata(input: $input) {
              key
              data
            }
          }
        `,
        variables: {
          input: {
            globalId: this.me.activeAccount.globalId,
            flowIdentifier: this.$route.query.flow,
            key: 'tc_sg_selected_business_entity_type',
            data: JSON.stringify({ selectedBusinessEntity: this.selectedBusinessEntity }),
          },
        },
        context: {
          headers: {
            'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
          },
        },
      })
    },
  },
}
</script>
